<template>
  <div class="name-correction">
    <div class="back-row" @click="goBack">
      <i class="el-icon-arrow-left"></i>
      <span>Back to Service Tickets</span>
    </div>

    <div class="header">
      <p class="title">Name Change Request</p>
      <p class="subtitle">
        This ticket requests a correction to a name that has been registered
        for one of your programs. Your new name will be reviewed and applied
        manually by our staff after payment is confirmed.
      </p>
      <div class="fee-box">
        <div>
          <div class="fee-label">Service fee</div>
          <div class="fee-why" @click="showWhy = !showWhy">
            Why is there a fee? <i :class="showWhy ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
          </div>
        </div>
        <div class="fee-amount">USD 40</div>
      </div>
      <div v-if="showWhy" class="why-panel">
        <p>
          Correcting a registered name is not automatic — each request
          requires a staff member to review your emails, update the record
          across our systems, and reply to confirm the change. For free
          registration programs, the fee covers that work.
        </p>
        <ol>
          <li>There is a meaningful cost behind every correction — staff time and coordination are real work.</li>
          <li>The fee encourages responsible registration. You can certainly avoid it by being careful when entering your information.</li>
          <li>The fee is based on the reasonable time required to complete the correction and the corresponding staff cost at our headquarters.</li>
        </ol>
      </div>
    </div>

    <el-form :model="form" :rules="rules" ref="form" label-position="top" class="form-body">
      <el-form-item label="Which registration is this correction for?" prop="application_id">
        <el-select
          v-model="form.application_id"
          placeholder="Select a registration"
          style="width: 100%;"
          :loading="appsLoading"
          @change="onApplicationChange">
          <el-option
            v-for="app in applications"
            :key="app._id.$id"
            :label="applicationLabel(app)"
            :value="app._id.$id">
          </el-option>
        </el-select>
        <p class="hint" v-if="!appsLoading && !applications.length">
          No registrations found on your account.
        </p>
      </el-form-item>

      <div class="current-name" v-if="currentName">
        <div class="current-name-title">Currently on file</div>
        <div class="current-name-row">
          <span class="label">Native Language:</span>
          <span class="value">{{ currentName.native }}</span>
        </div>
        <div class="current-name-row">
          <span class="label">English:</span>
          <span class="value">{{ currentName.en }}</span>
        </div>
      </div>

      <div class="section-title">Corrected name</div>
      <div class="row">
        <el-form-item label="Given Name (Native Language)" prop="new_givenName">
          <el-input v-model="form.new_givenName" placeholder="Please input"></el-input>
        </el-form-item>
        <el-form-item label="Last Name (Native Language)" prop="new_lastName">
          <el-input v-model="form.new_lastName" placeholder="Please input"></el-input>
        </el-form-item>
      </div>
      <div class="row">
        <el-form-item label="Given Name (EN)" prop="new_givenName_pinyin">
          <el-input v-model="form.new_givenName_pinyin" placeholder="Please input"></el-input>
        </el-form-item>
        <el-form-item label="Last Name (EN)" prop="new_lastName_pinyin">
          <el-input v-model="form.new_lastName_pinyin" placeholder="Please input"></el-input>
        </el-form-item>
      </div>

      <el-form-item label="Reason for correction (optional)" prop="reason">
        <el-input
          v-model="form.reason"
          type="textarea"
          :rows="3"
          placeholder="Briefly explain why the correction is needed. This helps our staff process it faster."></el-input>
      </el-form-item>

      <div class="confirm-row" @click="confirmed = !confirmed">
        <img
          class="chk"
          v-if="!confirmed"
          :src="require('../../assets/image/unchecked.png')" />
        <img class="chk" v-else :src="require('../../assets/image/checked.png')" />
        <span>
          I have double-checked the corrected name above. I understand that
          the USD 40 fee compensates staff time and will not be refunded once
          the correction has been processed.
        </span>
      </div>

      <div class="actions">
        <el-button @click="goBack">Cancel</el-button>
        <el-button type="primary" :loading="submitting" @click="submit">Submit & Pay</el-button>
      </div>
    </el-form>
  </div>
</template>

<script>
import { createServiceTicket, getStripeUrlForServiceTicket, getStudentApplications } from '../../api/eae'
import { getUserId } from '../../utils/store'

export default {
  name: 'NameCorrectionTicket',
  data() {
    return {
      showWhy: false,
      confirmed: false,
      submitting: false,
      appsLoading: false,
      applications: [],
      form: {
        application_id: '',
        new_givenName: '',
        new_lastName: '',
        new_givenName_pinyin: '',
        new_lastName_pinyin: '',
        reason: '',
      },
      rules: {
        application_id: [{ required: true, message: 'Please select the registration to correct', trigger: 'change' }],
        new_givenName: [{ required: true, message: 'Please input', trigger: 'blur' }],
        new_lastName: [{ required: true, message: 'Please input', trigger: 'blur' }],
        new_givenName_pinyin: [{ required: true, message: 'Please input', trigger: 'blur' }],
        new_lastName_pinyin: [{ required: true, message: 'Please input', trigger: 'blur' }],
      },
    }
  },
  computed: {
    selectedApp() {
      return this.applications.find((a) => a._id.$id === this.form.application_id) || null
    },
    currentName() {
      const a = this.selectedApp
      if (!a) return null
      const native = [a.student_lastName, a.student_givenName].filter(Boolean).join(' ') || '—'
      const en = [a.student_lastName_pinyin, a.student_givenName_pinyin].filter(Boolean).join(' ') || '—'
      return { native, en }
    },
  },
  mounted() {
    this.fetchApplications()
  },
  methods: {
    fetchApplications() {
      const student_id = getUserId()
      if (!student_id) return
      this.appsLoading = true
      getStudentApplications(student_id)
        .then((res) => {
          const list = (res.data && res.data.data) || []
          this.applications = Array.isArray(list) ? list : []
        })
        .finally(() => {
          this.appsLoading = false
        })
    },
    applicationLabel(app) {
      const project = app.application_project || app.project_name || 'Registration'
      const studentName =
        [app.student_lastName_pinyin, app.student_givenName_pinyin].filter(Boolean).join(' ') ||
        [app.student_lastName, app.student_givenName].filter(Boolean).join(' ') ||
        ''
      return studentName ? `${project} — ${studentName}` : project
    },
    onApplicationChange() {
      // Pre-fill the "new" fields with current values so the user only
      // has to edit the incorrect ones, not retype the whole name.
      const a = this.selectedApp
      if (!a) return
      this.form.new_givenName = a.student_givenName || ''
      this.form.new_lastName = a.student_lastName || ''
      this.form.new_givenName_pinyin = a.student_givenName_pinyin || ''
      this.form.new_lastName_pinyin = a.student_lastName_pinyin || ''
    },
    goBack() {
      this.$router.push({ path: '/home/serviceTickets' })
    },
    submit() {
      if (!this.confirmed) {
        this.$message.warning('Please confirm the corrected name and the fee terms.')
        return
      }
      this.$refs.form.validate((valid) => {
        if (!valid) return
        const a = this.selectedApp
        const payload = {
          current_givenName: a ? a.student_givenName || '' : '',
          current_lastName: a ? a.student_lastName || '' : '',
          current_givenName_pinyin: a ? a.student_givenName_pinyin || '' : '',
          current_lastName_pinyin: a ? a.student_lastName_pinyin || '' : '',
          new_givenName: this.form.new_givenName,
          new_lastName: this.form.new_lastName,
          new_givenName_pinyin: this.form.new_givenName_pinyin,
          new_lastName_pinyin: this.form.new_lastName_pinyin,
          reason: this.form.reason,
        }
        this.submitting = true
        createServiceTicket(getUserId(), 'name_correction', this.form.application_id, payload)
          .then((res) => {
            const data = res.data
            if (!data || data.code !== 0) {
              this.$message.error((data && data.msg) || 'Failed to create ticket')
              return Promise.reject()
            }
            const ticket_id = data.data && data.data._id && data.data._id.$id
            if (!ticket_id) {
              this.$message.error('Failed to create ticket (no id)')
              return Promise.reject()
            }
            return getStripeUrlForServiceTicket(ticket_id)
          })
          .then((res) => {
            if (!res) return
            const data = res.data
            if (!data || data.code !== 0 || !data.data || !data.data.url) {
              this.$message.error((data && data.msg) || 'Failed to start payment')
              return
            }
            window.location.href = data.data.url
          })
          .finally(() => {
            this.submitting = false
          })
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.name-correction {
  padding: 24px 32px;
  max-width: 860px;
  margin: 0 auto;
}

.back-row {
  color: #ff8e3b;
  font-size: 13px;
  cursor: pointer;
  margin-bottom: 16px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.header {
  margin-bottom: 24px;

  .title {
    font-size: 22px;
    font-weight: bold;
    color: #ff8e3b;
    margin-bottom: 6px;
  }

  .subtitle {
    color: #666;
    font-size: 14px;
    line-height: 1.6;
  }
}

.fee-box {
  margin-top: 14px;
  padding: 14px 18px;
  background: #fff5f4;
  border-radius: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .fee-label {
    color: #333;
    font-weight: bold;
    font-size: 14px;
    margin-bottom: 2px;
  }

  .fee-why {
    color: #ff8e3b;
    font-size: 12px;
    cursor: pointer;
  }

  .fee-amount {
    font-size: 22px;
    font-weight: bold;
    color: #ff8e3b;
  }
}

.why-panel {
  margin-top: 10px;
  padding: 14px 18px;
  background: #f7f7f9;
  border-radius: 8px;
  font-size: 13px;
  color: #555;
  line-height: 1.6;

  ol {
    margin: 8px 0 0 18px;
  }

  li {
    margin-bottom: 6px;
  }
}

.form-body {
  margin-top: 8px;
}

.section-title {
  font-size: 15px;
  font-weight: bold;
  color: #333;
  margin: 16px 0 10px;
}

.row {
  display: flex;
  gap: 20px;

  > .el-form-item {
    flex: 1;
  }
}

.current-name {
  margin: 0 0 20px;
  padding: 12px 16px;
  background: #fafafa;
  border-radius: 8px;

  .current-name-title {
    font-size: 12px;
    color: #999;
    margin-bottom: 6px;
  }

  .current-name-row {
    font-size: 14px;
    padding: 3px 0;

    .label {
      color: #888;
      margin-right: 8px;
    }

    .value {
      color: #333;
      font-weight: 500;
    }
  }
}

.confirm-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 16px 0 24px;
  padding: 12px 14px;
  background: #fffbf5;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  color: #555;
  line-height: 1.5;

  .chk {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    margin-top: 2px;
  }
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 10px;
}

.hint {
  color: #999;
  font-size: 12px;
  margin-top: 4px;
}
</style>
